import supabase from './supabase';
import { getMapboxToken } from './mapService';

// Route Connections Service
export class RouteConnectionService {
  // Create a connection between two routes
  static async createConnection(routeId, connectedRouteId, transportMode = 'driving') {
    try {
      const { data, error } = await supabase
        .from('route_connections')
        .insert([{
          route_id: routeId,
          connected_route_id: connectedRouteId,
          transport_mode: transportMode,
          connection_order: await this.getNextConnectionOrder(routeId)
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating route connection:', error);
      throw error;
    }
  }

  // Get all connections for a route
  static async getRouteConnections(routeId) {
    try {
      const { data, error } = await supabase
        .from('route_connections')
        .select(`
          *,
          connected_route:routes!route_connections_connected_route_id_fkey (
            id,
            name,
            description,
            status,
            is_private,
            user_id
          )
        `)
        .eq('route_id', routeId)
        .order('connection_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching route connections:', error);
      throw error;
    }
  }

  // Get the next connection order for a route
  static async getNextConnectionOrder(routeId) {
    try {
      const { data, error } = await supabase
        .rpc('get_next_connection_order', { route_uuid: routeId });

      if (error) throw error;
      return data || 1;
    } catch (error) {
      console.error('Error getting next connection order:', error);
      return 1;
    }
  }

  // Delete a route connection
  static async deleteConnection(connectionId) {
    try {
      const { error } = await supabase
        .from('route_connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting route connection:', error);
      throw error;
    }
  }

  // Update connection order
  static async updateConnectionOrder(connectionId, newOrder) {
    try {
      const { data, error } = await supabase
        .from('route_connections')
        .update({ connection_order: newOrder })
        .eq('id', connectionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating connection order:', error);
      throw error;
    }
  }

  // Get available routes for connection (respecting privacy)
  static async getAvailableRoutesForConnection(currentUserId, currentRouteId) {
    try {
      // First get user's own routes
      const { data: ownRoutes, error: ownError } = await supabase
        .from('routes')
        .select(`
          id,
          name,
          status,
          is_private,
          user_id,
          created_at
        `)
        .eq('user_id', currentUserId)
        .neq('id', currentRouteId);

      if (ownError) throw ownError;

      // Then get public routes from people they follow
      // First get the list of followed user IDs
      const { data: followedUserIds, error: followedIdsError } = await supabase
        .from('followers')
        .select('followed_id')
        .eq('follower_id', currentUserId);

      if (followedIdsError) throw followedIdsError;

      let followedRoutes = [];
      if (followedUserIds && followedUserIds.length > 0) {
        const followedIds = followedUserIds.map(f => f.followed_id);
        const { data: routes, error: followedError } = await supabase
          .from('routes')
          .select(`
            id,
            name,
            status,
            is_private,
            user_id,
            created_at
          `)
          .eq('is_private', false)
          .neq('id', currentRouteId)
          .in('user_id', followedIds);

        if (followedError) throw followedError;
        followedRoutes = routes || [];
      }

      // Combine and sort
      const allRoutes = [...(ownRoutes || []), ...(followedRoutes || [])];
      return allRoutes.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error fetching available routes for connection:', error);
      throw error;
    }
  }

  // Check if user can access a specific route
  static async canAccessRoute(routeId, userId) {
    try {
      const { data, error } = await supabase
        .rpc('can_access_route', { 
          route_uuid: routeId, 
          user_uuid: userId 
        });

      if (error) throw error;
      return data || false;
    } catch (error) {
      console.error('Error checking route access:', error);
      return false;
    }
  }
}

// Route Directions Service
export class RouteDirectionsService {
  // Generate directions between two route points using Mapbox Directions API
  static async generateDirections(startPoint, endPoint, transportMode = 'driving') {
    try {
      const token = getMapboxToken();
      if (!token) {
        throw new Error('Mapbox token not available');
      }

      const [startLng, startLat] = startPoint;
      const [endLng, endLat] = endPoint;

      const url = `https://api.mapbox.com/directions/v5/mapbox/${transportMode}/${startLng},${startLat};${endLng},${endLat}?geometries=geojson&access_token=${token}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Directions API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating directions:', error);
      throw error;
    }
  }

  // Save directions to database
  static async saveDirections(connectionId, directionsData) {
    try {
      const steps = directionsData.routes[0]?.legs[0]?.steps || [];
      const directions = [];

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const direction = {
          connection_id: connectionId,
          step_order: i + 1,
          instruction: step.maneuver.instruction,
          distance: step.distance,
          duration: step.duration,
          maneuver_type: step.maneuver.type,
          maneuver_instruction: step.maneuver.instruction,
          start_coordinates: `(${step.maneuver.location[0]}, ${step.maneuver.location[1]})`,
          end_coordinates: `(${step.maneuver.location[0]}, ${step.maneuver.location[1]})`,
          route_geometry: step.geometry
        };
        directions.push(direction);
      }

      const { data, error } = await supabase
        .from('route_directions')
        .insert(directions)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving directions:', error);
      throw error;
    }
  }

  // Get directions for a connection
  static async getDirections(connectionId) {
    try {
      const { data, error } = await supabase
        .from('route_directions')
        .select('*')
        .eq('connection_id', connectionId)
        .order('step_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching directions:', error);
      throw error;
    }
  }

  // Generate and save directions for a connection
  static async generateAndSaveDirections(connectionId, startPoint, endPoint, transportMode = 'driving') {
    try {
      // First, delete any existing directions for this connection
      await this.deleteDirections(connectionId);

      // Generate new directions
      const directionsData = await this.generateDirections(startPoint, endPoint, transportMode);

      // Save directions to database
      const savedDirections = await this.saveDirections(connectionId, directionsData);

      return savedDirections;
    } catch (error) {
      console.error('Error generating and saving directions:', error);
      throw error;
    }
  }

  // Delete directions for a connection
  static async deleteDirections(connectionId) {
    try {
      const { error } = await supabase
        .from('route_directions')
        .delete()
        .eq('connection_id', connectionId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting directions:', error);
      throw error;
    }
  }
}

// Route Journeys Service
export class RouteJourneyService {
  // Create a new journey
  static async createJourney(journeyData) {
    try {
      const { data, error } = await supabase
        .from('route_journeys')
        .insert([journeyData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating journey:', error);
      throw error;
    }
  }

  // Get all journeys for a user (including public journeys from people they follow)
  static async getUserJourneys(userId) {
    try {
      // First get user's own journeys
      const { data: ownJourneys, error: ownError } = await supabase
        .from('route_journeys')
        .select(`
          *,
          journey_routes (
            route_order,
            route:routes (
              id,
              name,
              is_private,
              user_id
            )
          )
        `)
        .eq('user_id', userId);

      if (ownError) throw ownError;

      // Then get public journeys from people they follow
      // First get the list of followed user IDs
      const { data: followedUserIds, error: followedIdsError } = await supabase
        .from('followers')
        .select('followed_id')
        .eq('follower_id', userId);

      if (followedIdsError) throw followedIdsError;

      let followedJourneys = [];
      if (followedUserIds && followedUserIds.length > 0) {
        const followedIds = followedUserIds.map(f => f.followed_id);
        const { data: journeys, error: followedError } = await supabase
          .from('route_journeys')
          .select(`
            *,
            journey_routes (
              route_order,
              route:routes (
                id,
                name,
                is_private,
                user_id
              )
            )
          `)
          .eq('is_private', false)
          .in('user_id', followedIds);

        if (followedError) throw followedError;
        followedJourneys = journeys || [];
      }

      // Combine and sort
      const allJourneys = [...(ownJourneys || []), ...(followedJourneys || [])];
      return allJourneys.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (error) {
      console.error('Error fetching user journeys:', error);
      throw error;
    }
  }

  // Add a route to a journey
  static async addRouteToJourney(journeyId, routeId) {
    try {
      const routeOrder = await this.getNextJourneyRouteOrder(journeyId);
      
      const { data, error } = await supabase
        .from('journey_routes')
        .insert([{
          journey_id: journeyId,
          route_id: routeId,
          route_order: routeOrder
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding route to journey:', error);
      throw error;
    }
  }

  // Get the next route order for a journey
  static async getNextJourneyRouteOrder(journeyId) {
    try {
      const { data, error } = await supabase
        .rpc('get_next_journey_route_order', { journey_uuid: journeyId });

      if (error) throw error;
      return data || 1;
    } catch (error) {
      console.error('Error getting next journey route order:', error);
      return 1;
    }
  }

  // Get a complete journey with all routes and directions
  static async getCompleteJourney(journeyId) {
    try {
      const { data, error } = await supabase
        .from('route_journeys')
        .select(`
          *,
          journey_routes (
            route_order,
            route:routes (
              id,
              name,
              is_private,
              user_id,
              route_points (
                id,
                name,
                latitude,
                longitude,
                point_order
              )
            )
          )
        `)
        .eq('id', journeyId)
        .single();

      if (error) throw error;

      // Get connections and directions for all routes in the journey
      if (data.journey_routes) {
        for (const journeyRoute of data.journey_routes) {
          const connections = await RouteConnectionService.getRouteConnections(journeyRoute.route.id);
          journeyRoute.route.connections = connections;

          // Get directions for each connection
          for (const connection of connections) {
            const directions = await RouteDirectionsService.getDirections(connection.id);
            connection.directions = directions;
          }
        }
      }

      return data;
    } catch (error) {
      console.error('Error fetching complete journey:', error);
      throw error;
    }
  }

  // Update journey totals
  static async updateJourneyTotals(journeyId) {
    try {
      const { error } = await supabase
        .rpc('update_journey_totals', { journey_uuid: journeyId });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating journey totals:', error);
      throw error;
    }
  }

  // Delete a journey
  static async deleteJourney(journeyId) {
    try {
      const { error } = await supabase
        .from('route_journeys')
        .delete()
        .eq('id', journeyId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting journey:', error);
      throw error;
    }
  }
}

// Utility function to get route endpoints (first and last points)
export const getRouteEndpoints = (routePoints) => {
  if (!routePoints || routePoints.length === 0) {
    return { start: null, end: null };
  }

  const sortedPoints = routePoints.sort((a, b) => a.point_order - b.point_order);
  const start = sortedPoints[0];
  const end = sortedPoints[sortedPoints.length - 1];

  return {
    start: start.latitude && start.longitude ? [start.longitude, start.latitude] : null,
    end: end.latitude && end.longitude ? [end.longitude, end.latitude] : null
  };
};

// Utility function to format distance and duration
export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
};

export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};
